import React, { useEffect, useState, useRef } from 'react';
import ReactDOM from 'react-dom';
import { useDashboard } from '../context/DashboardContext';
import { useAuth } from '../context/AuthContext';

const Breadcrumbs = () => {
  const { currentView, currentCity, currentImpact, goHome, selectCity, selectImpact, getOrderedImpacts, hasIndicatorData } = useDashboard();
  const { isAdmin } = useAuth();
  const breadcrumbRef = useRef(null);
  const impactDropdownRef = useRef(null);
  const [topPosition, setTopPosition] = useState(102);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 480);
  const [isTablet, setIsTablet] = useState(window.innerWidth <= 768 && window.innerWidth > 480);
  const [isSmallMobile, setIsSmallMobile] = useState(window.innerWidth <= 375);
  const [adminSelectedCity, setAdminSelectedCity] = useState(localStorage.getItem('adminSelectedCity') || null);
  const [impactDropdownOpen, setImpactDropdownOpen] = useState(false);
  const [impactDropdownPosition, setImpactDropdownPosition] = useState(null);

  useEffect(() => {
    const calculatePosition = () => {
      const header = document.querySelector('header');
      const ticker = document.querySelector('.ticker-wrap');
      
      if (breadcrumbRef.current) {
        let newTop = 102; // Default fallback
        
        if (header && ticker) {
          // Use getBoundingClientRect for more accurate measurement
          const headerRect = header.getBoundingClientRect();
          const tickerRect = ticker.getBoundingClientRect();
          
          // Calculate breadcrumb position as ticker's bottom edge (flush connection)
          // tickerRect.top is the ticker's top position (which equals header height)
          // tickerRect.height is the ticker's height
          // So tickerRect.top + tickerRect.height gives us the exact bottom edge
          newTop = tickerRect.top + tickerRect.height;
          
          // Ensure minimum visibility - breadcrumb should always be visible
          if (newTop < 50) {
            // Fallback: use header + ticker heights if rect calculation fails
            const headerHeight = headerRect.height || header.offsetHeight || 70;
            const tickerHeight = tickerRect.height || ticker.offsetHeight || 32;
            newTop = headerHeight + tickerHeight;
          }
        }
        
        setTopPosition(newTop);
        
        // Force all styles directly on the element with responsive padding
        const element = breadcrumbRef.current;
        const width = window.innerWidth;
        const isSmallMobile = width <= 375;
        const isMobile = width <= 480;
        const isTablet = width <= 768 && width > 480;
        
        const padding = isSmallMobile ? '0.3rem 0.5rem' : (isMobile ? '0.35rem 0.75rem' : (isTablet ? '0.4rem 1rem' : '0.5rem 2rem'));
        const fontSize = isSmallMobile ? '0.75rem' : (isMobile ? '0.8rem' : (isTablet ? '0.85rem' : '0.9rem'));
        
        element.style.cssText = `
          position: fixed !important;
          top: ${newTop}px !important;
          left: 0 !important;
          right: 0 !important;
          width: 100% !important;
          display: block !important;
          visibility: visible !important;
          opacity: 1 !important;
          z-index: 998 !important;
          background: #e9ecef !important;
          border-bottom: 1px solid #ddd !important;
          border-top: none !important;
          color: #666 !important;
          padding: ${padding} !important;
          font-size: ${fontSize} !important;
          box-sizing: border-box !important;
          overflow-x: auto !important;
          white-space: nowrap !important;
          -webkit-overflow-scrolling: touch !important;
        `;
      }
    };

    // Calculate on mount and resize
    calculatePosition();
    
    // Multiple attempts to ensure it's positioned correctly
    const timeouts = [
      setTimeout(calculatePosition, 50),
      setTimeout(calculatePosition, 100),
      setTimeout(calculatePosition, 200),
      setTimeout(calculatePosition, 500),
      setTimeout(calculatePosition, 1000)
    ];
    
    const handleResize = () => {
      const width = window.innerWidth;
      setIsMobile(width <= 480);
      setIsTablet(width <= 768 && width > 480);
      setIsSmallMobile(width <= 375);
      calculatePosition();
    };

    window.addEventListener('resize', handleResize);
    window.addEventListener('load', calculatePosition);
    
    // Also listen for orientation changes on mobile
    window.addEventListener('orientationchange', () => {
      setTimeout(() => {
        const width = window.innerWidth;
        setIsMobile(width <= 480);
        setIsTablet(width <= 768 && width > 480);
        setIsSmallMobile(width <= 375);
        calculatePosition();
      }, 100);
    });

    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('load', calculatePosition);
      window.removeEventListener('orientationchange', handleResize);
      timeouts.forEach(timeout => clearTimeout(timeout));
    };
  }, [currentView]);

  // Listen for admin city selection
  useEffect(() => {
    const handleAdminCitySelected = (e) => {
      setAdminSelectedCity(e.detail.city);
    };
    
    window.addEventListener('adminCitySelected', handleAdminCitySelected);
    return () => window.removeEventListener('adminCitySelected', handleAdminCitySelected);
  }, []);

  // Close impact dropdown when clicking outside
  useEffect(() => {
    if (!impactDropdownOpen) return;
    const handleClickOutside = (e) => {
      const wrap = impactDropdownRef.current;
      const portalId = 'breadcrumb-impact-dropdown-portal';
      const portalEl = document.getElementById(portalId);
      const isClickInWrap = wrap && wrap.contains(e.target);
      const isClickInDropdown = portalEl && portalEl.contains(e.target);
      if (!isClickInWrap && !isClickInDropdown) {
        setImpactDropdownOpen(false);
        setImpactDropdownPosition(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [impactDropdownOpen]);

  // Update dropdown position when open (e.g. on scroll/resize)
  useEffect(() => {
    if (!impactDropdownOpen || !impactDropdownRef.current) return;
    const updatePosition = () => {
      if (impactDropdownRef.current) {
        const rect = impactDropdownRef.current.getBoundingClientRect();
        setImpactDropdownPosition({ top: rect.bottom + 2, left: rect.left });
      }
    };
    updatePosition();
    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);
    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [impactDropdownOpen]);

  const renderBreadcrumbs = () => {
    // Check if we're on admin route
    const isAdminRoute = window.location.pathname.includes('/admin');
    
    // Admin panel breadcrumbs
    if (isAdminRoute && isAdmin()) {
      if (adminSelectedCity) {
        return (
          <>
            <span className="back-btn" onClick={() => {
              window.history.pushState({}, '', '/');
              window.dispatchEvent(new Event('navigation'));
            }}>Home</span>
            {' / '}
            <span className="back-btn" onClick={() => {
              localStorage.removeItem('adminSelectedCity');
              setAdminSelectedCity(null);
              // Dispatch event to reset AdminPanel state
              window.dispatchEvent(new CustomEvent('adminCitySelected', { detail: { city: null } }));
              window.history.pushState({}, '', '/admin');
              window.dispatchEvent(new Event('navigation'));
            }}>Super_admin</span>
            {' / '}
            <span className="current-crumb">{adminSelectedCity}</span>
          </>
        );
      } else {
        return (
          <>
            <span className="back-btn" onClick={() => {
              window.history.pushState({}, '', '/');
              window.dispatchEvent(new Event('navigation'));
            }}>Home</span>
            {' / '}
            <span className="current-crumb">Super_admin</span>
          </>
        );
      }
    }
    
    // Regular dashboard breadcrumbs
    switch (currentView) {
      case 'home':
        return <span className="current-crumb">Home</span>;
      
      case 'summary':
        return (
          <>
            <span className="back-btn" onClick={goHome}>Home</span>
            {' / '}
            <span className="current-crumb">{currentCity}</span>
          </>
        );
      
      case 'detail': {
        const orderedImpacts = currentCity ? (getOrderedImpacts(currentCity) || []) : [];
        const showImpactDropdown = orderedImpacts.length > 0 && currentImpact;
        const openDropdown = () => {
          if (impactDropdownRef.current) {
            const rect = impactDropdownRef.current.getBoundingClientRect();
            setImpactDropdownPosition({ top: rect.bottom + 2, left: rect.left });
          }
          setImpactDropdownOpen(true);
        };
        const closeDropdown = () => {
          setImpactDropdownOpen(false);
          setImpactDropdownPosition(null);
        };
        const dropdownList = impactDropdownOpen && impactDropdownPosition && showImpactDropdown && (
          ReactDOM.createPortal(
            <div
              id="breadcrumb-impact-dropdown-portal"
              style={{
                position: 'fixed',
                top: impactDropdownPosition.top,
                left: impactDropdownPosition.left,
                zIndex: 10000,
              }}
            >
              <ul
                className="breadcrumb-impact-dropdown"
                style={{
                  margin: 0,
                  padding: '0.25rem 0',
                  listStyle: 'none',
                  background: '#fff',
                  border: '1px solid #ddd',
                  borderRadius: '6px',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                  minWidth: '200px',
                  maxHeight: '70vh',
                  overflowY: 'auto',
                  fontSize: '0.9em',
                }}
              >
                {orderedImpacts.map((impact) => {
                  const hasAnyIndicatorData = currentCity && impact.sectors?.some((sector) =>
                    sector.indicators?.some((ind) => hasIndicatorData(currentCity, ind.name))
                  );
                  const isLocked = !hasAnyIndicatorData;
                  return (
                    <li
                      key={impact.id}
                      onClick={() => {
                        if (isLocked) return;
                        selectImpact(impact.id, currentCity);
                        closeDropdown();
                      }}
                      style={{
                        padding: '0.4rem 0.75rem',
                        cursor: isLocked ? 'not-allowed' : 'pointer',
                        color: impact.id === currentImpact?.id ? 'var(--accent)' : (isLocked ? '#999' : '#333'),
                        fontWeight: impact.id === currentImpact?.id ? 700 : 500,
                        background: impact.id === currentImpact?.id ? 'rgba(52, 152, 219, 0.08)' : 'transparent',
                        opacity: isLocked ? 0.75 : 1,
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.35rem',
                      }}
                      onMouseEnter={(e) => {
                        if (isLocked) return;
                        if (impact.id !== currentImpact?.id) {
                          e.currentTarget.style.background = '#f0f7ff';
                          e.currentTarget.style.color = 'var(--accent)';
                        }
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = impact.id === currentImpact?.id ? 'rgba(52, 152, 219, 0.08)' : 'transparent';
                        e.currentTarget.style.color = impact.id === currentImpact?.id ? 'var(--accent)' : (isLocked ? '#999' : '#333');
                      }}
                      title={isLocked ? 'No indicator data available for this impact' : undefined}
                    >
                      {isLocked && (
                        <img
                          src="/lock.png"
                          alt="Locked"
                          style={{
                            width: '16px',
                            height: '16px',
                            flexShrink: 0,
                            opacity: 0.7,
                            objectFit: 'contain',
                          }}
                        />
                      )}
                      {impact.title}
                    </li>
                  );
                })}
              </ul>
            </div>,
            document.body
          )
        );
        return (
          <>
            {dropdownList}
            <span className="back-btn" onClick={goHome}>Home</span>
            {' / '}
            <span className="back-btn" onClick={() => selectCity(currentCity)}>{currentCity}</span>
            {' / '}
            {showImpactDropdown ? (
              <span ref={impactDropdownRef} className="breadcrumb-impact-dropdown-wrap" style={{ position: 'relative', display: 'inline-flex', alignItems: 'center' }}>
                <span
                  className="current-crumb breadcrumb-impact-trigger"
                  onClick={() => (impactDropdownOpen ? closeDropdown() : openDropdown())}
                  style={{ cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '0.25rem' }}
                  title="Switch to another impact"
                >
                  {currentImpact.title}
                  <span style={{ fontSize: '0.65em', opacity: 0.8 }} aria-hidden>▼</span>
                </span>
              </span>
            ) : (
              <span className="current-crumb">{currentImpact?.title || 'Details'}</span>
            )}
          </>
        );
      }
      
      default:
        return <span className="current-crumb">Home</span>;
    }
  };

  return (
    <div 
      ref={breadcrumbRef}
      className="breadcrumb-bar" 
      style={{ 
        display: 'block', 
        visibility: 'visible', 
        opacity: 1,
        top: `${topPosition}px`,
        position: 'fixed',
        zIndex: 998,
        left: 0,
        right: 0,
        width: '100%',
        background: '#e9ecef',
        borderBottom: '1px solid #ddd',
        color: '#666',
        padding: isSmallMobile ? '0.3rem 0.5rem' : (isMobile ? '0.35rem 0.75rem' : (isTablet ? '0.4rem 1rem' : '0.5rem 2rem')),
        boxSizing: 'border-box',
        fontSize: isSmallMobile ? '0.75rem' : (isMobile ? '0.8rem' : (isTablet ? '0.85rem' : '0.9rem'))
      }}
    >
      {renderBreadcrumbs()}
    </div>
  );
};

export default Breadcrumbs;

